// ============================================================================
// src/app/navigation/appLocation.ts — the facade's location-read hook.
// ============================================================================
// WHY THIS EXISTS
// Every pathname/search/hash/state read in the tree goes through this seam so
// the future router cutover can swap the location source in exactly one
// non-root module. The returned object is a pinned subset of the active
// navigation driver's location: {pathname, search, hash, state}. `key` is
// deliberately NOT exposed — zero consumers read it today, and location keys
// are not stable across router implementations.
//
// Reads are pass-throughs of the current values (state arrives exactly as it
// was written — the write side already normalized it). The subset object is
// memoized on the underlying location, so its identity is stable across
// re-renders until a real navigation occurs — effect dependency behavior is
// unchanged from reading the raw location's primitives.
//
// toAppLocation is the pure subset function (unit-tested directly);
// useAppLocation is the hook binding over the resolved driver's location hook.
// ============================================================================

import {useMemo} from 'react';
import type {DriverLocation} from './navigationDriver.js';
import {resolveNavigationDriver} from './navigationDriver.js';

// The pinned subset. Every field is passed straight through from the driver
// location, so `state` stays `unknown` — the write side normalized it and no
// consumer may read it without proving its shape.
export interface AppLocation {
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
}

// The parameter type is the contract; the runtime guard stays because the
// callers are still JavaScript and a driver is registered, not compiled in.
export function toAppLocation(location: DriverLocation): AppLocation {
  if (!location || typeof location !== 'object' || Array.isArray(location)) {
    throw new TypeError('useAppLocation requires a driver location object');
  }
  return {
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
    state: location.state,
  };
}

export function useAppLocation(): AppLocation {
  const location = resolveNavigationDriver().useLocationValue();
  return useMemo(() => toAppLocation(location), [location]);
}
